if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Atelier: one delegated listener, so a thumbnail added later (reconcile, after a size change) works too.
        // Capture: it runs before listeners lower down (<at-hero-gallery>'s counter reads the thumbnail it sets).
        this.elements.thumbnails.addEventListener(
          'click',
          (event) => {
            const item = event.target.closest && event.target.closest('[data-target]');
            const button = item && event.target.closest('button');
            if (item && button && button === item.querySelector('button')) this.setActiveMedia(item.dataset.target, false);
          },
          true
        );
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          // Atelier: a photo can have no thumbnail (hide_variants leaves a size's own photo out of the thumbnails), which
          // threw here and left the size "Unavailable".
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        return activeMedia.dataset.mediaId;
      }

      // Atelier: makes the slides and the thumbnails match another render of this gallery (the section re-render after
      // a size change: the size's own photo first, hide_variants dropping the others), in one place. Items are keyed by
      // data-media-id (slides) and data-target (thumbnails); ones already here are moved, not re-created, so their
      // loaded photos stay; missing ones are imported from the render, extra ones removed.
      reconcile(source, preservePosition = false) {
        if (!source) return;
        [
          [this.elements.viewer, source.querySelector('[id^="GalleryViewer"]'), 'mediaId', 'data-media-id'],
          [this.elements.thumbnails, source.querySelector('[id^="GalleryThumbnails"]'), 'target', 'data-target'],
        ].forEach(([here, there, key, attr]) => {
          const list = here && here.querySelector(`[${attr}]`)?.parentElement;
          const from = there && there.querySelector(`[${attr}]`)?.parentElement;
          if (!list || !from) return;
          const current = new Map(Array.from(list.querySelectorAll(`:scope > [${attr}]`), (el) => [el.dataset[key], el]));
          const wanted = Array.from(from.querySelectorAll(`:scope > [${attr}]`));
          const keep = new Set(wanted.map((el) => el.dataset[key]));
          // WebKit does not keep the same photo in view when slides move. An agreeing size response keeps the
          // nearest visible item at its old offset in the strip, even if the store's order has changed.
          const left = list.scrollLeft;
          let anchor = null;
          if (preservePosition && list.scrollWidth > list.clientWidth) {
            for (const el of current.values()) {
              if (!el.clientWidth) continue;
              if (!anchor || Math.abs(el.offsetLeft - left) < Math.abs(anchor.offsetLeft - left)) anchor = el;
            }
          }
          const offset = anchor ? anchor.offsetLeft - left : 0;
          let changed = false;
          current.forEach((el, id) => {
            if (keep.has(id)) return;
            el.remove();
            changed = true;
          });
          wanted.forEach((src, i) => {
            const el = current.get(src.dataset[key]) || document.importNode(src, true);
            const at = list.querySelectorAll(`:scope > [${attr}]`)[i];
            if (at !== el) {
              list.insertBefore(el, at || null);
              changed = true;
            }
          });
          if (changed && anchor?.parentElement === list) {
            list.scrollTo({ left: anchor.offsetLeft - offset, behavior: 'instant' });
          }
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
